'use client'

import { Link, useRouter, usePathname } from '@/i18n/routing'
import { useTranslations, useLocale } from 'next-intl'
import { getPostHref } from '@/utilities/getPostHref'
import { PayloadImage } from '@/components/Media/PayloadImage'
import { format } from 'date-fns'
import type { Post } from '@/payload-types'

interface BlogGridProps {
  posts: Post[]
  totalPages: number
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export function BlogGrid({
  posts,
  totalPages,
  currentPage,
  hasNextPage,
  hasPrevPage,
}: BlogGridProps) {
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations('Blog')

  const handlePageChange = (page: number) => {
    const params: string[] = []

    // Extract current filters from pathname
    const pathSegments = pathname.split('/').filter(Boolean)
    const blogIndex = pathSegments.indexOf('blog')
    const currentParams = pathSegments.slice(blogIndex + 1)

    // Parse current parameters
    let search = ''
    let category = ''

    for (let i = 0; i < currentParams.length; i += 2) {
      const key = currentParams[i]
      const value = currentParams[i + 1]

      if (key === 'search' && value) search = decodeURIComponent(value)
      if (key === 'category' && value) category = decodeURIComponent(value)
    }

    // Build new params
    if (search) {
      params.push('search', encodeURIComponent(search))
    }

    if (category) {
      params.push('category', encodeURIComponent(category))
    }

    if (page > 1) {
      params.push('page', page.toString())
    }

    const newPath = params.length > 0 ? `/blog/${params.join('/')}` : '/blog'
    router.push(newPath)
  }

  const locale = useLocale()

  return (
    <div>
      <div className="space-y-8">
        {posts.map((post) => {
          const href = getPostHref(post, locale as 'en' | 'cs')
          return (
            <article key={post.id} className="group">
              <Link href={href} className="block">
                <div className="flex flex-col md:flex-row gap-8 p-8 rounded-2xl bg-black/40 backdrop-blur-sm border border-white/10 hover:border-strandforge-purple/50 transition-all duration-300 hover:shadow-2xl hover:shadow-strandforge-purple/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-strandforge-purple/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />

                  {post.heroImage && typeof post.heroImage === 'object' && (
                    <div className="md:w-1/3 aspect-[16/9] relative rounded-xl overflow-hidden">
                      <PayloadImage
                        src={post.heroImage.url || ''}
                        alt={post.heroImage.alt || post.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-strandforge-purple/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  )}

                  <div className="md:w-2/3 flex flex-col justify-between relative z-10">
                    <div>

                      <h3 className="text-2xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-strandforge-purple-dark group-hover:via-strandforge-purple group-hover:to-strandforge-purple-light group-hover:bg-clip-text transition-all duration-300 mb-4 leading-tight">
                        {post.title}
                      </h3>

                      {post.excerpt && (
                        <p className="text-gray-300 mb-6 line-clamp-3 text-lg leading-relaxed">
                          {post.excerpt}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6 text-gray-400">
                        {post.populatedAuthors && post.populatedAuthors.length > 0 && (
                          <div className="flex items-center bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                            <span className="text-sm font-medium">
                              {t('by')}{' '}
                              {post.populatedAuthors.map((author) => author.name).join(', ')}
                            </span>
                          </div>
                        )}

                        {post.publishedAt && (
                          <div className="flex items-center bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                            <time className="text-sm font-medium">
                              {format(new Date(post.publishedAt), 'MMM dd, yyyy')}
                            </time>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center text-strandforge-purple-light group-hover:text-white transition-colors font-medium">
                        <span className="mr-2">{t('readMore')}</span>
                        <svg
                          className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </article>
          )
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-16 flex justify-center">
          <nav className="flex items-center space-x-3 bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!hasPrevPage}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {t('previous')}
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)

              if (!showPage) {
                if (page === currentPage - 3 || page === currentPage + 3) {
                  return (
                    <span key={page} className="px-3 py-2 text-gray-500">
                      ...
                    </span>
                  )
                }
                return null
              }

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    page === currentPage
                      ? 'bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light text-white shadow-lg shadow-strandforge-purple/30'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {page}
                </button>
              )
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNextPage}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {t('next')}
            </button>
          </nav>
        </div>
      )}

      {posts.length === 0 && (
        <div className="text-center py-20">
          <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-12 max-w-2xl mx-auto">
            <div className="text-gray-400 text-xl mb-6">{t('noPosts')}</div>
            <Link
              href="/blog"
              className="bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light hover:from-strandforge-purple-dark hover:to-strandforge-purple text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-strandforge-purple/30 inline-flex items-center group"
            >
              <span className="mr-2">{t('viewAllPosts')}</span>
              <svg
                className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
